import m from 'mithril'
import {
	type FileSizeUnit,
	getFileSizeDisplayUnit,
	getFormattedFileSizeInUnit,
} from '@client-shared/utils/file-objects'
import { changeUploadStatus } from '../components/file-library-modal/file-library-modal.util'
import OriginDestination from '@shared-types/origin-destination'

/**
 * Mock upload items for development that represent uploads in different states
 */
const mock_upload_items: UploadItem[] = [
	{
		id: '96639226-126d-4829-91cb-0617f621dd5a',
		status: 'post-processing',
		filename: 'questionably_anti.zip',
		total_bytes: 1055567817,
		uploaded_bytes: 1055567817,
		display_values: { uploaded_size: 1055.6, total_size: 1055.6, size_unit: 'mb' },
	},
	{
		id: '300a49ac-d006-49e9-a056-74c1b11e4c35',
		error_message: 'Mock Post Processing Error Message',
		status: 'post-processing-error',
		filename: 'haze_geez.zip',
		total_bytes: 878725257,
		uploaded_bytes: 164265172,
		display_values: { uploaded_size: 164.3, total_size: 878.7, size_unit: 'mb' },
	},
	{
		id: '9a811a6f-799c-42f0-8252-29d70724b6c6',
		status: 'uploading',
		filename: 'beret_overcook_shell.zip',
		total_bytes: 810899332,
		uploaded_bytes: 436962386,
		display_values: { uploaded_size: 436, total_size: 810.9, size_unit: 'mb' },
	},
	{
		id: '300a49ac-d006-49e9-a056-74c1b11e4c34',
		error_message: 'Mock Error Message',
		status: 'error',
		filename: 'haze_geez.zip',
		total_bytes: 878725257,
		uploaded_bytes: 164265172,
		display_values: { uploaded_size: 164.3, total_size: 878.7, size_unit: 'mb' },
	},
	{
		id: '61e3690f-b3e5-4619-93d6-9ed35b23f612',
		status: 'canceled',
		filename: 'intently_why_yippee.json',
		total_bytes: 762620186,
		uploaded_bytes: 587208626,
		display_values: { uploaded_size: 587.2, total_size: 762.6, size_unit: 'mb' },
	},
	{
		id: 'c9a691fe-b9f8-406b-a48f-6487d7d03019',
		error_message: '',
		status: 'complete',
		filename: 'necklace_bah_who.json',
		total_bytes: 226741372,
		uploaded_bytes: 226741372,
		display_values: { uploaded_size: 226.7, total_size: 226.7, size_unit: 'mb' },
	},
]

export interface UploadItem {
	id: string
	status: 'uploading' | 'complete' | 'canceled' | 'error' | 'post-processing' | 'post-processing-error'
	filename: string
	total_bytes: number
	uploaded_bytes: number
	stats_message?: string
	error_message?: string
	post_processing_type?: string
	last_update?: Date
	metadata?: Record<string, unknown>
	display_values: {
		uploaded_size: number
		total_size: number
		size_unit: FileSizeUnit
	}
}

type UploadHandlerEventType =
	| 'start'
	| 'progress'
	| 'error'
	| 'canceled'
	| 'post-processing'
	| 'post-processing-error'
	| 'complete'

export type UpdateHandlerFunc = (upload_item_id?: string, event_type?: UploadHandlerEventType) => void

export type UploadItemPostProcessor = (item: UploadItem, emitUpdate: () => void) => Promise<void>

class UploadsManager {
	// #upload_items: UploadItem[] = mock_upload_items
	#upload_items: UploadItem[] = []
	#update_handlers: UpdateHandlerFunc[] = []
	#xhr_by_id: Map<string, XMLHttpRequest> = new Map()

	constructor() {
		/**
		 * Add a beforeunload event to enable a prompt before closing the page while
		 * uploads are in progress
		 */
		window.addEventListener('beforeunload', (e: BeforeUnloadEvent) => {
			// NOTE: There doesn't seem to be a way to display a custom message
			// in modern browsers. All that will be shown is the default browser
			// "your changes might not be saved"-style message.

			if (this.uploads_in_progress) {
				e.preventDefault()
			}
		})
	}

	start(
		upload_item_id: string,
		file: File,
		url: string,
		params: {
			method?: string
			file_key?: string
			post_processors?: UploadItemPostProcessor[]
			replaces_existing_item_id?: string // TODO: use this to indicate restarts
		} = {},
	) {
		const { method = 'POST', file_key = 'file', post_processors = [] } = params

		const file_size_display_unit = getFileSizeDisplayUnit(file.size)

		const upload_item: UploadItem = {
			id: upload_item_id,
			status: 'uploading',
			filename: file.name,
			total_bytes: file.size,
			uploaded_bytes: 0,
			last_update: new Date(),
			display_values: {
				uploaded_size: 0,
				total_size: getFormattedFileSizeInUnit(file.size, file_size_display_unit),
				size_unit: file_size_display_unit,
			},
		}

		this.#upload_items.unshift(upload_item)

		m.request({
			method,
			url,
			body: {},
			serialize: () => {
				// NOTE: the serialize method must be used in order to ensure
				// that mithril will send the request as xhr.send(file)

				return file
			},
			config: (xhr: XMLHttpRequest) => {
				xhr.upload.addEventListener('abort', () => {
					upload_item.status = 'canceled'
					upload_item.last_update = new Date()

					this.emitUpdate(upload_item.id, 'canceled')
				})

				xhr.upload.addEventListener('error', () => {
					upload_item.status = 'error'
					upload_item.last_update = new Date()
					upload_item.error_message = 'upload error'

					this.emitUpdate(upload_item.id, 'error')
				})

				xhr.upload.addEventListener('timeout', () => {
					upload_item.status = 'error'
					upload_item.last_update = new Date()
					upload_item.error_message = 'network timeout'

					this.emitUpdate(upload_item.id, 'error')
				})

				xhr.upload.addEventListener('progress', (e) => {
					if (upload_item.status !== 'uploading') {
						console.warn('progess event but item is not uploading', upload_item)
						return
					}

					upload_item.uploaded_bytes = e.loaded
					upload_item.display_values.uploaded_size = getFormattedFileSizeInUnit(
						e.loaded,
						upload_item.display_values.size_unit,
					)

					upload_item.total_bytes = e.total
					upload_item.display_values.total_size = getFormattedFileSizeInUnit(
						e.total,
						upload_item.display_values.size_unit,
					)

					// if (upload_item.uploaded_bytes === upload_item.total_bytes) {
					// 	upload_item.status = 'complete'
					// }

					upload_item.last_update = new Date()

					this.emitUpdate(upload_item.id, 'progress')
				})

				this.#xhr_by_id.set(upload_item.id, xhr)

				return xhr
			},
		})
			.then(async () => {
				if (post_processors.length) {
					upload_item.status = 'post-processing'
				}

				upload_item.last_update = new Date()

				for (const processor of post_processors) {
					try {
						await processor(upload_item, () => {
							this.emitUpdate(upload_item.id, 'post-processing')
						})
					} catch (err) {
						upload_item.status = 'post-processing-error'

						if (err instanceof Error) {
							throw err
						} else if (typeof err === 'string') {
							throw new Error(err)
						}

						throw new Error('Post-processing error occurred. Please try again.')
					}
				}

				if (upload_item.status !== 'complete') {
					upload_item.status = 'complete'
				}

				this.emitUpdate(upload_item.id, 'complete')

				// add 3s timeout to auto-remove completed entries
				setTimeout(() => {
					this.clear(upload_item.id)
				}, 3000)
			})
			.catch((err) => {
				if (!['error', 'post-processing-error'].includes(upload_item.status)) {
					upload_item.status = 'error'
				}

				if (err instanceof Error) {
					upload_item.error_message = err.message ?? 'An error occurred'

					// NOTE: m.request can throw an error object with the string value 'null'
					if (upload_item.error_message.toLowerCase() === 'null') {
						upload_item.error_message = 'An error occurred'
					}
				} else if (typeof err === 'string') {
					upload_item.error_message = err
				} else {
					upload_item.error_message = 'An error occurred'
				}

				this.emitUpdate(
					upload_item.id,
					upload_item.status === 'post-processing-error' ? 'post-processing-error' : 'error',
				)
			})

		this.emitUpdate(upload_item.id, 'start')

		return upload_item
	}

	cancel(upload_id: string) {
		const upload_item = this.#upload_items.find((item) => item.id === upload_id)

		if (!upload_item || upload_item.status !== 'uploading') {
			return
		}

		this.#xhr_by_id.get(upload_id)?.abort()
	}

	clear(upload_id: string) {
		const removable_statuses: UploadItem['status'][] = ['canceled', 'complete', 'error', 'post-processing-error']

		this.#upload_items = this.#upload_items.filter(
			(item) => !(item.id === upload_id && removable_statuses.includes(item.status)),
		)

		this.emitUpdate()
	}

	// NOTE: Restart will be a bit more involved since it needs to request a new upload URL.
	// The way to do this would be to add another type of callback handler on this manager
	// alongside "onUpdate" ("onRequestRestart" for example) that provides the upload item ID
	// to a callback on a component where the UploadsManager.start method is called.
	//
	/*
	restartUpload(upload_id: string) {
		const restart_statuses: UploadItem['status'][] = ['canceled', 'error', 'post-processing-error']

		const item_idx = this.#upload_items.findIndex((item) => item.id === upload_id)

		if (item_idx === -1 || !restart_statuses.includes(this.#upload_items[item_idx].status)) {
			// item is not eligible for a restart
			return
		}
	}
	*/

	clearInactiveItems() {
		this.#upload_items = this.#upload_items.filter(
			(item) => item.status === 'uploading' || item.status === 'post-processing',
		)
	}

	get uploads_in_progress() {
		return this.#upload_items.some((item) => item.status === 'uploading')
	}

	get upload_items() {
		return this.#upload_items
	}

	onUpdate(handler: UpdateHandlerFunc) {
		if (this.#update_handlers.includes(handler)) {
			return
		}

		this.#update_handlers.push(handler)
	}

	removeUpdateHandler(remove_handler: UpdateHandlerFunc) {
		this.#update_handlers = this.#update_handlers.filter((handler) => handler !== remove_handler)
	}

	emitUpdate(upload_item_id?: string, event_type?: UploadHandlerEventType) {
		// check if this is an event type that should trigger an API call to update the upload item status
		if (upload_item_id && event_type) {
			const update_status_map: Partial<Record<typeof event_type, OriginDestination.Enums.UploadedFileUploadStatus>> = {
				start: OriginDestination.Enums.UploadedFileUploadStatus.UPLOADING,
				canceled: OriginDestination.Enums.UploadedFileUploadStatus.CANCELED,
				error: OriginDestination.Enums.UploadedFileUploadStatus.FAILED,
				complete: OriginDestination.Enums.UploadedFileUploadStatus.SUCCESSFUL,
				'post-processing-error': OriginDestination.Enums.UploadedFileUploadStatus.SUCCESSFUL, // NOTE: Even though the post-processing (ex: validation) failed, the upload was still successful
			} as const

			if (update_status_map[event_type]) {
				changeUploadStatus(upload_item_id, update_status_map[event_type]).catch((err) =>
					console.error(
						`failed to update upload item status: ${upload_item_id} | ${update_status_map[event_type]}`,
						err,
					),
				)
			}
		}

		// call all of the registered update handlers
		for (const handler of this.#update_handlers) {
			handler(upload_item_id, event_type)
		}
	}
}

export default new UploadsManager()
